/**
 * <TranscriptStream>
 *
 * Chronological list of `<TranscriptBubble>` rows fed by the caption list its
 * parent passes in (Phase 2 item 2.10 of the AI Scribe rollout). Purely
 * presentational: the live `useLiveTranscription` subscription is owned by the
 * parent `<ZoomPanel>`, NOT here, so each caption is captured and POSTed to the
 * server exactly once. (A previous version called `useLiveTranscription` here
 * too — a second live subscription that double-recorded every transcript line.)
 * Owns the auto-scroll-to-latest behavior — the parent `<ZoomPanel>` body
 * provides the scroll container height; this component just keeps the bottom of
 * the list pinned to the viewport as new captions arrive.
 *
 * Variants
 *  - `light` (default) — historical white-card visual used by any non-Zoom
 *    transcript surface.
 *  - `dark` — §7 of the new ZoomPanel redesign. Pure transparent
 *    background (parent panel is slate-900), PROVIDER/PATIENT speaker
 *    diarization labels, animated 3-dot "streaming" loader, and an indigo
 *    "Transcript complete" banner when `isComplete` is true.
 *
 * Auto-scroll
 *  - Each caption pushes the bottom sentinel into view via `scrollIntoView`.
 */
import { useEffect, useRef } from 'react'
import { TranscriptBubble, type TranscriptBubbleVariant } from './TranscriptBubble'
import { type LiveCaption } from '@/pages/Call/hooks/useLiveTranscription'
import { scribeText } from './scribeText'

export type TranscriptStreamProps = {
  /**
   * Caption list to render. Supplied by the parent, which owns the single
   * `useLiveTranscription` subscription. Defaults to empty.
   */
  captions?: LiveCaption[]
  /** Visual variant; defaults to `light` for backwards compat. */
  variant?: TranscriptBubbleVariant
  /**
   * When true, render the animated 3-dot streaming loader at the bottom of
   * the list. Only applies to the `dark` variant per the §7 spec.
   */
  isStreaming?: boolean
  /**
   * When true, render the indigo "Transcript complete — click End to save
   * note" banner at the bottom of the list. Only applies to the `dark`
   * variant per the §7 spec (ChartPage.tsx:5508-5513). Mutually exclusive
   * with `isStreaming` — when both are true, the streaming dots win.
   */
  isComplete?: boolean
}

export function TranscriptStream({
  captions = [],
  variant = 'light',
  isStreaming = false,
  isComplete = false,
}: TranscriptStreamProps) {
  const bottomRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (captions.length === 0) {
      return
    }
    bottomRef.current?.scrollIntoView({ block: 'end' })
  }, [captions.length])

  if (captions.length === 0) {
    if (variant === 'dark') {
      return (
        <div
          data-testid="transcript-stream"
          data-variant="dark"
          className={`flex h-full items-start ${scribeText.body} text-slate-600`}
        >
          <div data-testid="transcript-placeholder">no captions yet</div>
        </div>
      )
    }
    return (
      <div
        data-testid="transcript-stream"
        data-variant="light"
        className="flex h-full items-start text-sm text-[#7D7987]"
      >
        <div data-testid="transcript-placeholder">no captions yet</div>
      </div>
    )
  }

  return (
    <div data-testid="transcript-stream" data-variant={variant} className="flex flex-col">
      {captions.map((c, idx) => (
        <TranscriptBubble
          // Captions don't carry a stable id today. Index + timestamp is good
          // enough because the list is append-only — captions never reorder
          // or get deleted mid-session.
          key={`${idx}-${c.timestamp}`}
          speaker={c.speaker}
          text={c.text}
          timestamp={c.timestamp}
          variant={variant}
        />
      ))}
      {variant === 'dark' && isStreaming && (
        <div
          data-testid="transcript-stream-dots"
          aria-hidden="true"
          className="flex gap-1 py-[2px]"
        >
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className="block h-1 w-1 rounded-full bg-slate-700"
              style={{
                animation: 'transcriptDot 1.4s ease-in-out infinite',
                animationDelay: `${i * 0.2}s`,
              }}
            />
          ))}
        </div>
      )}
      {variant === 'dark' && isComplete && !isStreaming && (
        <div
          data-testid="transcript-stream-complete"
          className={`mt-1 flex items-center gap-[6px] rounded-md border border-indigo-500/20 bg-indigo-500/10 px-[10px] py-2 ${scribeText.label} text-[#a5b4fc]`}
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden="true"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
          Transcript complete — click End to save note
        </div>
      )}
      {/* Bottom sentinel — scrollIntoView target. Zero-height + aria-hidden
          so it doesn't affect screen readers or layout. */}
      <div ref={bottomRef} aria-hidden="true" data-testid="transcript-stream-bottom" />
    </div>
  )
}
